import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateChildFn, GuardResult, Router } from '@angular/router';
import { filter, firstValueFrom, take } from 'rxjs';
import {
  agentRouteCommands,
  agentRoutePaths,
  SALES_MODULE_KEY,
} from '../agent/agent-route-paths';
import { MeDto } from '../connectors/backend';
import { AuthService } from '../services/auth-service';
import { CompanyService } from '../services/company.service';
import { PermissionsService } from '../services/permissions.service';
import { SectionVisibilityService } from '../services/section-visibility.service';
import { waitForMeAndCompany } from './company.guard';

const agentAppPaths: string[] = [
  agentRoutePaths.root,
  agentRoutePaths.unavailable,
];

function landing(
  injector: Injector,
  me: MeDto,
  companyId: string,
): GuardResult | Promise<GuardResult> {
  const router = injector.get(Router);
  const toApp = () => router.createUrlTree(agentRouteCommands.dashboard());
  if (me.companies[companyId]?.landing === SALES_MODULE_KEY) return toApp();

  const query = injector.get(PermissionsService).myPermissionsQuery;
  const sectionVisibility = injector.get(SectionVisibilityService);
  // A failed permissions read must not block every PIM page.
  const bySections = (): GuardResult =>
    query.isSuccess() && sectionVisibility.getFirstAccessibleSection() === null
      ? toApp()
      : true;

  if (query.isSuccess() || query.isError()) return bySections();
  return firstValueFrom(
    toObservable(
      computed(() => query.isSuccess() || query.isError()),
      { injector },
    ).pipe(filter(Boolean), take(1)),
  ).then(bySections);
}

/**
 * Sends to the agents app a user whose company lands there or who has no PIM section, so a
 * module-only rep never reaches the PIM shell and a user with no grant leaves the redirect
 * loop between `/` and the products section; the app's guard then says why it may not enter.
 * Synchronous once `me`, the company and the permissions are in: it runs on every navigation
 * inside the PIM shell.
 */
export const moduleLandingGuard: CanActivateChildFn = (_, state) => {
  const router = inject(Router);
  const firstSegment = router.parseUrl(state.url).root.children['primary']
    ?.segments[0]?.path;
  if (firstSegment && agentAppPaths.includes(firstSegment)) return true;

  const injector = inject(Injector);
  const me = inject(AuthService).me();
  const companyId = inject(CompanyService).companyId();
  if (me && companyId) return landing(injector, me, companyId);
  return waitForMeAndCompany(injector).then((known) =>
    landing(injector, known.me, known.companyId),
  );
};
